import {
  infer_capability_tags,
  infer_pin_role,
  normalize_booleanish,
  normalize_pin_role_value,
  normalize_string_array,
  parse_nullable_number,
} from './pinInference';

const CONNECTION_ROLE_ALIASES = {
  ground: 'ground',
  'power-input': 'power',
  'power-output': 'power',
  'power-input-output': 'power',
  power: 'power',
  gpio: 'gpio',
  digital: 'gpio',
  'analog-input': 'analog',
  analog: 'analog',
  adc: 'analog',
  ain: 'analog',
  'i2c-sda': 'i2c-sda',
  'i2c-scl': 'i2c-scl',
  i2c: 'i2c',
  'spi-mosi': 'spi-mosi',
  'spi-miso': 'spi-miso',
  'spi-sck': 'spi-sck',
  'spi-cs': 'spi-cs',
  spi: 'spi',
  'uart-tx': 'uart-tx',
  'uart-rx': 'uart-rx',
  uart: 'uart',
  pwm: 'pwm',
  interrupt: 'interrupt',
  control: 'special',
  reset: 'special',
  enable: 'special',
  'flash-data': 'special',
  special: 'special',
  'resistor-terminal': 'resistor-terminal',
  'led-anode': 'led-anode',
  'led-cathode': 'led-cathode',
  passive: 'passive',
};

const get_voltage_bounds = (pin) => {
  const min = parse_nullable_number(pin?.voltage_min_v || pin?.voltageMinV);
  const max = parse_nullable_number(pin?.voltage_max_v || pin?.voltageMaxV);

  if (min !== null || max !== null) {
    return { min, max };
  }

  const voltage_range = String(pin?.voltage_range || pin?.voltageRange || '').trim();

  if (!voltage_range) {
    return { min: null, max: null };
  }

  const range_match = voltage_range.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);

  if (range_match) {
    return {
      min: Number.parseFloat(range_match[1]),
      max: Number.parseFloat(range_match[2]),
    };
  }

  const single = Number.parseFloat(voltage_range.replace(/[^\d.]/g, ''));

  return Number.isFinite(single)
    ? { min: single, max: single }
    : { min: null, max: null };
};

export const get_pin_value = (pin, keys) => {
  for (const key of keys) {
    const value = pin?.[key];

    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  return '';
};

export const normalize_pin_role = (role) => {
  return normalize_pin_role_value(role);
};

const normalize_connection_role = (role) => {
  const normalized = normalize_pin_role(role);

  return CONNECTION_ROLE_ALIASES[normalized] || normalized;
};

export const get_pin_role = (pin) => {
  const explicit_role = normalize_pin_role(
    get_pin_value(pin, ['pin_role', 'pinRole'])
  );

  if (explicit_role) {
    return explicit_role;
  }

  return normalize_pin_role(infer_pin_role(pin).value);
};

export const get_pin_capabilities = (pin) => {
  const explicit_tags = normalize_string_array(pin?.capability_tags ?? pin?.capabilityTags)
    .map((tag) => normalize_pin_role(tag) || String(tag).trim().toLowerCase())
    .filter(Boolean);

  const inferred_tags = infer_capability_tags({
    ...pin,
    pin_role: get_pin_role(pin),
  })
    .map((tag) => normalize_pin_role(tag) || String(tag).trim().toLowerCase())
    .filter(Boolean);

  return Array.from(new Set([...explicit_tags, ...inferred_tags]));
};

export const get_preferred_connection_role = (pin) => {
  const explicit_role = normalize_connection_role(get_pin_role(pin));
  const capabilities = get_pin_capabilities(pin).map((tag) => normalize_connection_role(tag));
  const priority = [
    'i2c-sda',
    'i2c-scl',
    'spi-mosi',
    'spi-miso',
    'spi-sck',
    'spi-cs',
    'uart-tx',
    'uart-rx',
    'analog',
    'pwm',
    'interrupt',
    'gpio',
  ];

  const explicit_priority_role = priority.find((role) => explicit_role === role);

  if (explicit_priority_role) {
    return explicit_priority_role;
  }

  const capability_priority_role = priority.find((role) => capabilities.includes(role));

  if (capability_priority_role) {
    return capability_priority_role;
  }

  return explicit_role;
};

export const is_signal_role = (role) => {
  const normalized = normalize_connection_role(role);

  return normalized !== 'power' && normalized !== 'ground';
};

export const are_voltages_compatible = (source_pin, target_pin) => {
  const source_voltage = get_voltage_bounds(source_pin);
  const target_voltage = get_voltage_bounds(target_pin);

  if (source_voltage.min === null && source_voltage.max === null) {
    return true;
  }

  if (target_voltage.min === null && target_voltage.max === null) {
    return true;
  }

  const source_min = source_voltage.min !== null ? source_voltage.min : source_voltage.max;
  const source_max = source_voltage.max !== null ? source_voltage.max : source_voltage.min;
  const target_min = target_voltage.min !== null ? target_voltage.min : target_voltage.max;
  const target_max = target_voltage.max !== null ? target_voltage.max : target_voltage.min;

  return source_max >= target_min && target_max >= source_min;
};

export const are_currents_compatible = (source_pin, target_pin) => {
  const source_current = parse_nullable_number(
    source_pin?.current_limit_ma ||
    source_pin?.currentLimitMa ||
    source_pin?.current_limit ||
    source_pin?.currentLimit
  );
  const target_current = parse_nullable_number(
    target_pin?.current_limit_ma ||
    target_pin?.currentLimitMa ||
    target_pin?.current_limit ||
    target_pin?.currentLimit
  );

  if (source_current === null || target_current === null) {
    return true;
  }

  return source_current <= target_current;
};

export const are_pins_compatible = (source_pin, target_pin) => {
  const source_role = get_preferred_connection_role(source_pin);
  const target_role = get_preferred_connection_role(target_pin);
  const target_capabilities = get_pin_capabilities(target_pin).map((tag) => normalize_connection_role(tag));

  const target_supports_role = (role) => {
    const normalized_role = normalize_connection_role(role);

    return (
      target_role === normalized_role ||
      target_capabilities.includes(normalized_role)
    );
  };

  const compatibility_matrix = {
    gpio: ['gpio', 'i2c', 'spi', 'uart', 'pwm', 'interrupt'],
    analog: ['analog', 'gpio'],
    power: ['power'],
    ground: ['ground'],
    i2c: ['i2c', 'i2c-sda', 'i2c-scl'],
    'i2c-sda': ['i2c-sda'],
    'i2c-scl': ['i2c-scl'],
    spi: ['spi', 'spi-mosi', 'spi-miso', 'spi-sck', 'spi-cs'],
    'spi-mosi': ['spi-mosi'],
    'spi-miso': ['spi-miso'],
    'spi-sck': ['spi-sck'],
    'spi-cs': ['spi-cs', 'gpio'],
    uart: ['uart', 'uart-rx', 'uart-tx'],
    'uart-tx': ['uart-rx'],
    'uart-rx': ['uart-tx'],
    pwm: ['pwm', 'gpio'],
    interrupt: ['interrupt', 'gpio'],
    special: ['special'],
    passive: ['passive'],
    'resistor-terminal': ['resistor-terminal', 'passive'],
    'led-anode': ['led-anode'],
    'led-cathode': ['led-cathode'],
  };

  const compatible_roles = compatibility_matrix[source_role] || [];

  if (
    !compatible_roles.includes(target_role) &&
    !compatible_roles.some((role) => target_supports_role(role))
  ) {
    return false;
  }

  if (!are_voltages_compatible(source_pin, target_pin)) {
    return false;
  }

  if (!are_currents_compatible(source_pin, target_pin)) {
    return false;
  }

  const source_voltage = get_voltage_bounds(source_pin);
  const target_voltage = get_voltage_bounds(target_pin);
  const target_is_5v_tolerant = normalize_booleanish(
    target_pin?.is_5v_tolerant ?? target_pin?.is5vTolerant
  );

  if (
    is_signal_role(source_role) &&
    is_signal_role(target_role) &&
    source_voltage.max !== null &&
    target_voltage.max !== null &&
    source_voltage.max > 3.6 &&
    target_voltage.max <= 3.6 &&
    target_is_5v_tolerant === false
  ) {
    return false;
  }

  return true;
};

export const calculate_connection_score = (source_pin, target_pin) => {
  if (!are_pins_compatible(source_pin, target_pin)) {
    return 0;
  }

  let score = 0;

  const source_role = get_preferred_connection_role(source_pin);
  const target_role = get_preferred_connection_role(target_pin);

  if (source_role === target_role) {
    score += 100;
  }

  if (get_pin_role(source_pin) === get_pin_role(target_pin)) {
    score += 80;
  }

  const source_capabilities = get_pin_capabilities(source_pin).map((tag) => normalize_connection_role(tag));
  const target_capabilities = get_pin_capabilities(target_pin).map((tag) => normalize_connection_role(tag));

  if (source_capabilities.includes('i2c') && target_capabilities.includes('i2c')) {
    score += 50;
  }

  if (source_capabilities.includes('spi') && target_capabilities.includes('spi')) {
    score += 50;
  }

  if (source_capabilities.includes('uart') && target_capabilities.includes('uart')) {
    score += 50;
  }

  if (are_voltages_compatible(source_pin, target_pin)) {
    score += 20;
  }

  if (are_currents_compatible(source_pin, target_pin)) {
    score += 20;
  }

  return score;
};

export const get_connection_confidence = (source_pin, target_pin) => {
  if (!source_pin || !target_pin) {
    return 'unknown';
  }

  if (!are_pins_compatible(source_pin, target_pin)) {
    return 'low';
  }

  const score = calculate_connection_score(source_pin, target_pin);

  if (score >= 120) return 'high';
  if (score >= 60) return 'medium';

  return 'low';
};
